// trap-disarm.ts — shared shell for the two trap disarm minigames
// (plans/TRAP_DISARM_MINIGAME_PLAN_2026-07-31.md; owner-approved 2026-07-31).
//
// The trap commit (trap_truth_commit_inc.php, trap_disarm_begin) mints the board server-side and routes on the trap's NATURE:
//   mechanical → "Sear & Tension"  (mechanical.ts — Machining + Machining Tools)
//   magical    → "Glyph Unbinding" (magical.ts   — Magic + Enchanting Tools)
// This file owns everything the two games share: the side-info drawer CLAIM (the same shared-host
// hazard lockpick.ts documents at length — automatic websocket/GM repaints replace the canvas and
// orphan the attempt), the init/result/spring/cancel round-trips, the panel chrome, and the
// outcome rendering. The games own only their canvas + input + local feel; every OUTCOME is a
// POST and the server has the last word (geometry validation server-side).
//
// Stakes (owner ruling): botching does not cost a tool — the trap FIRES AT YOU (spring endpoint).
// A safe back-out resets fully; retrying is a fresh init with fresh AP.
//
// PUBLIC GLOBAL (window-attached): openTrapDisarmMinigame — the object card's Disarm button.

import { mountMechanicalGame } from './mechanical';
import { mountMagicalGame } from './magical';
import { mountRuneGame } from './rune';
import { actorCanReachTarget } from '../shared/proximity-browser';

// CLIENT TRUTH (Lane TRAPS S2/S3, 2026-09-28): every round-trip goes through the websocket bundle's trap adapter
// (websocket/trap-truth.ts -> options/game/actions/trap_truth_commit_inc.php). The retired disarm_{init,result,spring,cancel}
// endpoints are never called: the attempt is an opaque SESSION the adapter hands back (no server token), the board is still
// minted server-side, and the result is judged here first (janus-engine/traps/board-validators.ts) and re-judged by the commit.
type StepOutcome = { ok: true; body: Record<string, unknown> } | { ok: false; error: string; status: number; body: Record<string, unknown> | null };
type TrapAdapter = {
    ftTrapDisarmBegin?: (input: { encounterId: string; actorId: string; hostInventoryId: string; rollPrivate?: number })
        => Promise<{ outcome: 'handled'; session: unknown; config: Record<string, unknown> } | { outcome: 'refused'; error: string; message: string } | null>;
    ftTrapDisarmResult?: (session: unknown, submission: TrapDisarmResultPayload) => Promise<StepOutcome>;
    ftTrapDisarmSpring?: (session: unknown) => Promise<StepOutcome>;
    ftTrapDisarmCancel?: (session: unknown, keepalive?: boolean) => Promise<StepOutcome>;
};
const trapAdapter = (): TrapAdapter => window as unknown as TrapAdapter;

import type { MechGeometry, MagicThread, MagicGeometry, RuneStroke, RuneWard, RuneTensionModel, RuneDefinition, RuneGeometry } from '../janus-engine/traps/board-types.js';
export type { MechGeometry, MagicThread, MagicGeometry, RuneStroke, RuneWard, RuneTensionModel, RuneDefinition, RuneGeometry } from '../janus-engine/traps/board-types.js';

export interface TrapDisarmInitResponse {
    ok: boolean;
    error?: string;
    token: string;
    roll_id: string;
    host_name: string;
    /** Ground family for the backdrop (loam / cave / sand / …), resolved server-side from the
     *  trap host's environment. Absent on an older cached init payload — falls back to loam. */
    ground?: string;
    classification: 'mechanical' | 'magical';
    trap_dc: number;
    trap_tier: number;
    skill_tier: number;
    edge: number;
    geometry: MechGeometry | MagicGeometry | RuneGeometry;
    pulse_hot_seconds: number;
    // Per cue: EVERY ingested variant, shuffled by the server. A bare string or null is still
    // accepted so an older cached init payload keeps playing (see TrapDisarmSfx).
    sfx: Record<string, string | string[] | null>;
}

/** What a game hands back when its board is complete (the shell POSTs it to the result endpoint). */
export type TrapDisarmResultPayload =
    | { sear_positions: number[]; never_left_green: boolean }
    | { cut_sequence: number[]; clean_strokes: boolean; no_pulse_violations: boolean }
    | { runes_completed: number; never_left_green: boolean };

/** The shell services a mounted game through this — games never fetch anything themselves. */
export interface TrapDisarmShell {
    cfg: TrapDisarmInitResponse;
    sfx: TrapDisarmSfx;
    /** Board complete — submit the end-state. The shell renders the outcome. */
    submit(payload: TrapDisarmResultPayload): void;
    /** Catastrophe (strain expiry / discharge) — the trap fires at the disarmer. */
    spring(): void;
    /** Player backed out (Exit) or the panel was replaced under us — safe fail, full reset. */
    cancel(silent?: boolean): void;
    setHint(message: string): void;
    /** Games call this each animation frame; false = the panel is gone, stop and unwind. */
    stillMounted(): boolean;
}

/**
 * Which board an init payload describes — 'rune' | 'weave' | 'mechanical'.
 *
 * ⚠ Reads the GEOMETRY, never the classification. Magical traps mint a rune board as of
 * 2026-08-05, but a token minted before that switch carries weave geometry for its full 30-minute
 * TTL, so `classification === 'magical'` no longer identifies a single board. Mirrors the server's
 * ft_trap_minigame_board_of() exactly; the two must agree or a board is scored by the wrong rules.
 */
export function trapBoardOf(cfg: TrapDisarmInitResponse): 'rune' | 'weave' | 'mechanical' {
    const geo = cfg.geometry as Partial<RuneGeometry & MagicGeometry>;
    if (geo && (geo.board === 'rune' || Array.isArray(geo.runes))) return 'rune';
    if (geo && Array.isArray(geo.threads)) return 'weave';
    return 'mechanical';
}

export function trapEscapeHtml(s: unknown): string {
    const d = document.createElement('div');
    d.textContent = s == null ? '' : String(s);
    return d.innerHTML;
}

function trapSidelogContext(): { roll_private: 0 | 1 | 2; campaign_id: string; encounter_id: string; caster_id: string } {
    const sl = ftNormalizeEncounterContext(getSidelogContext() ?? {});
    return {
        roll_private: sl.roll_private,
        campaign_id: sl.campaign_id,
        encounter_id: sl.encounter_id,
        caster_id: String(window.ftGetActiveCasterId?.() ?? '').trim(),
    };
}

/**
 * Minigame SFX — the DigSfx/LockpickSfx shape: one-shots via throwaway Audio elements created in
 * user-gesture handlers, volume slaved to the soundboard's first effect channel, missing cue urls
 * (not ingested yet) silently degrade.
 */
export class TrapDisarmSfx {
    private urls: Record<string, string | string[] | null>;
    /** Last variant index played per cue, so a repeated cue never plays the same file twice running. */
    private lastPick: Record<string, number> = {};

    constructor(urls: unknown) {
        this.urls = urls && typeof urls === 'object' ? (urls as Record<string, string | string[] | null>) : {};
    }

    /**
     * One variant of a cue. The server now sends EVERY ingested variant per cue rather than rolling
     * one at init: 8 creaks were ingested and a whole board only ever heard one of them, which is
     * what made a dragged sear sound like a loop rather than a spring. Picking here, per PLAY,
     * makes the other seven reachable; the never-twice-running rule matters because a fast cue
     * (creak repeats every ~90-220ms) is exactly where an accidental repeat is audible.
     */
    private pick(cue: string): string | null {
        const entry = this.urls[cue];
        if (typeof entry === 'string') return entry || null;
        if (!Array.isArray(entry) || entry.length === 0) return null;
        if (entry.length === 1) return entry[0] ?? null;
        const last = this.lastPick[cue];
        let index = Math.floor(Math.random() * entry.length);
        if (index === last) index = (index + 1) % entry.length;
        this.lastPick[cue] = index;
        return entry[index] ?? null;
    }

    private masterVolume(): number {
        const ref = document.getElementById('audio_player_effect_1') as HTMLAudioElement | null;
        if (!ref) return 0.8;
        if (ref.muted || ref.volume <= 0) return 0;
        return ref.volume;
    }

    /** Is this cue actually ingested? Lets a caller fall back rather than play silence. */
    has(cue: string): boolean {
        const entry = this.urls[cue];
        return Array.isArray(entry) ? entry.length > 0 : !!entry;
    }

    play(cue: string, volMul = 0.7, rate = 1): void {
        const url = this.pick(cue);
        const vol = this.masterVolume() * volMul;
        if (!url || vol <= 0) return;
        try {
            const a = new Audio(url);
            a.volume = Math.min(1, vol);
            a.playbackRate = rate;
            window.ftRegisterSiteAudioTransient?.(a);
            void a.play().catch(() => { /* autoplay/abort — ignore */ });
        } catch { /* ignore */ }
    }
}

// ── side-info drawer ownership (lockpick.ts's hazard, dig.ts's implementation) ─────────────────
let trapSideInfoOwner: object | null = null;

function trapClaimSideInfo(host: HTMLElement, owner: object): void {
    trapSideInfoOwner = owner;
    window.__ftSideInfoMinigameActive = true;
    try { host.dataset.ftSideinfoOwner = 'trap-disarm'; } catch { /* non-fatal */ }
    try { window.ftClearOpenSideInfo?.(); } catch { /* non-fatal */ }
}

function trapReleaseSideInfo(owner: object): void {
    if (trapSideInfoOwner !== owner) return; // superseded by a newer panel — not ours to clear
    trapSideInfoOwner = null;
    window.__ftSideInfoMinigameActive = false;
    const host = document.getElementById('div_refresh_side_info');
    if (host) { try { delete host.dataset.ftSideinfoOwner; } catch { /* non-fatal */ } }
}

function trapErrorMessage(error: string | undefined): string {
    switch (error) {
        case 'not_armed':          return 'This trap is no longer armed.';
        case 'out_of_reach':       return 'You need to be within reach of the trap.';
        case 'missing_tools':      return 'You do not have the tools this trap\'s nature demands.';
        case 'tools_out_of_reach': return 'Your tools are here somewhere, but not within reach.';
        case 'session_expired':    return 'The attempt timed out. Open it again to retry.';
        case 'host_gone':          return 'The trapped object is gone.';
        case 'not_enough_aps':     return 'Not enough action points.';
        case 'verdict_mismatch':   return 'The mechanism did not respond the way you expected. Open it again to retry.';
        default:                   return 'Something went wrong: ' + String(error ?? 'unknown');
    }
}

class TrapDisarmPanel implements TrapDisarmShell {
    cfg: TrapDisarmInitResponse;
    sfx: TrapDisarmSfx;
    private wrap: HTMLElement;
    private finished = false;
    private session: unknown;

    constructor(wrap: HTMLElement, cfg: TrapDisarmInitResponse, session: unknown) {
        this.wrap = wrap;
        this.cfg = cfg;
        this.session = session;
        this.sfx = new TrapDisarmSfx(cfg.sfx);
        (wrap.querySelector('#trap_disarm_btn_exit') as HTMLButtonElement | null)
            ?.addEventListener('click', () => this.cancel());
    }

    stillMounted(): boolean {
        // The drawer is a shared host: a deliberate navigation (openItemInfo, panel close) replaces
        // our DOM. A detached wrap means the attempt is over — the game loop unwinds through cancel.
        return !this.finished && this.wrap.isConnected;
    }

    setHint(message: string): void {
        const hint = this.wrap.querySelector('#trap_disarm_hint');
        if (hint) hint.textContent = message;
    }

    private setError(message: string): void {
        const err = this.wrap.querySelector('#trap_disarm_err') as HTMLElement | null;
        if (!err) return;
        if (!message) { err.style.display = 'none'; err.textContent = ''; return; }
        err.style.display = '';
        err.textContent = message;
    }

    submit(payload: TrapDisarmResultPayload): void {
        if (this.finished) return;
        this.finished = true;
        void (async () => {
            try {
                const step = await trapAdapter().ftTrapDisarmResult?.(this.session, payload);
                if (!step) throw new Error('trap adapter unavailable');
                const data = (step.ok ? step.body : { ok: false, error: step.error }) as { ok: boolean; error?: string; success?: boolean; sprung?: boolean; perfect?: boolean };
                if (!data.ok) {
                    this.renderOutcome('warn', trapErrorMessage(data.error));
                    return;
                }
                if (data.success) {
                    // A RELEASE, not another seat. This used to fire a single 'click' — the exact
                    // cue a sear plays every time one seats — so finishing a whole board sounded
                    // identical to seating one more lever, and the win landed with no punctuation
                    // (owner, 2026-08-03). Two beats, pitched DOWN and slowing: the mechanism
                    // letting go rather than catching. Same two ingested cues, no new assets — the
                    // variant picker makes the pair different files, so it reads as one event.
                    // ⚠ Keep both rates near unity. These are short samples played through an
                    // <audio> element, so playbackRate resamples them — pitch far below ~0.7 and a
                    // percussive cue turns to mud rather than reading as "heavier" (owner,
                    // 2026-08-04, on the fumble cue that had the same fault).
                    // ⛔ The arcane boards resolve on an ARCANE cue. Testing classification alone
                    // is not enough now there are two magical boards — a rune board must land on
                    // 'bind' (its own glyph-seating beat), not on the weave's thread-snip.
                    const done = trapBoardOf(this.cfg) === 'rune' ? 'bind'
                        : this.cfg.classification === 'magical' ? 'sever' : 'click';
                    this.sfx.play(done, 0.85, 0.9);
                    window.setTimeout(() => this.sfx.play(done, 0.7, 0.74), 110);
                    this.renderOutcome('success', data.perfect
                        ? 'Disarmed — flawlessly. That counts as a natural 20.'
                        : 'The trap is disarmed. It can be recovered, or re-armed later.');
                } else if (data.sprung) {
                    this.sfx.play(this.cfg.classification === 'magical' ? 'discharge' : 'snap', 0.9);
                    this.renderOutcome('sprung', 'The trap goes off in your hands!');
                } else {
                    this.renderOutcome('warn', 'It slips back into its armed set. You can try again.');
                }
            } catch {
                // The response was lost, not necessarily the request — the server may already have
                // consumed the token and resolved the attempt. Claiming "still stands" and inviting
                // a resubmit was a lie the dead game loop couldn't honor anyway (Codex 2026-07-31
                // #5): report the honest unknown and close out.
                this.renderOutcome('warn', 'Connection lost — the outcome is uncertain. Re-open the object to see where things stand.');
            }
        })();
    }

    spring(): void {
        if (this.finished) return;
        this.finished = true;
        void (async () => {
            try {
                const step = await trapAdapter().ftTrapDisarmSpring?.(this.session);
                if (!step) throw new Error('trap adapter unavailable');
                const data = (step.ok ? step.body : { ok: false, error: step.error }) as { ok: boolean; error?: string; sprung?: boolean; reason?: string };
                if (data.ok && data.sprung) {
                    this.sfx.play(this.cfg.classification === 'magical' ? 'discharge' : 'snap', 0.9);
                    this.renderOutcome('sprung', 'The trap goes off in your hands!');
                } else if (data.ok) {
                    // Consumed by someone else since init — the fire found nothing left to do.
                    this.renderOutcome('warn', 'The mechanism gives way — but nothing happens. The trap was already spent.');
                } else {
                    // session_expired / host_gone / … — say what actually happened (Codex #8).
                    this.renderOutcome('warn', trapErrorMessage(data.error));
                }
            } catch {
                // The token expires on its own; the server never heard the botch, which is the
                // lying-client ceiling the plan accepts — an honest client just closes out.
                this.renderOutcome('warn', 'Connection lost.');
            }
        })();
    }

    cancel(silent = false): void {
        if (this.finished) return;
        this.finished = true;
        try {
            void trapAdapter().ftTrapDisarmCancel?.(this.session, true)?.catch(() => { /* the attempt expires on its own */ });
        } catch { /* ignore */ }
        if (silent) {
            // Panel already replaced under us — nothing left to paint into.
            trapReleaseSideInfo(this);
            return;
        }
        this.renderOutcome('warn', 'You ease off, and the trap settles back into its armed set.');
    }

    /** Endgame chrome: swap the canvas area for a closing line and release the drawer. */
    private renderOutcome(kind: 'success' | 'sprung' | 'warn', message: string): void {
        this.finished = true;
        const stage = this.wrap.querySelector('#trap_disarm_stage') as HTMLElement | null;
        const cls = kind === 'success' ? 'text-success' : kind === 'sprung' ? 'text-danger' : 'text-warning';
        if (stage) {
            stage.innerHTML = '<div class="p-3 text-center"><p class="mb-2 ' + cls + '">'
                + trapEscapeHtml(message)
                + '</p><button type="button" class="btn btn-sm btn-outline-secondary" id="trap_disarm_btn_close">Close</button></div>';
            (stage.querySelector('#trap_disarm_btn_close') as HTMLButtonElement | null)
                ?.addEventListener('click', () => {
                    trapReleaseSideInfo(this);
                    try { if (typeof closeSideInfoPanel === 'function') closeSideInfoPanel(); } catch { /* non-fatal */ }
                });
        }
        trapReleaseSideInfo(this);
        try { if (typeof refreshSidePanel === 'function') refreshSidePanel(); } catch { /* non-fatal */ }
    }
}

/** Double-click / double-tap dedupe: one init request at a time. */
let trapOpenInFlight = false;

async function openTrapDisarmMinigame(hostInventoryId: string): Promise<void> {
    if (!hostInventoryId || trapOpenInFlight) return;
    trapOpenInFlight = true;
    try {
        if (typeof forceOpenSideInfoPanel === 'function') forceOpenSideInfoPanel();
        const host = document.getElementById('div_refresh_side_info');
        if (host) {
            host.innerHTML =
                '<div class="p-3 text-center"><div class="spinner-border spinner-border-sm"></div><p class="small mt-2 mb-0">Studying the trap…</p></div>';
        }

        const ctx = trapSidelogContext();
        const within = await actorCanReachTarget(ctx.caster_id, hostInventoryId);
        if (within !== true) {
            if (host) host.textContent = within === false ? 'You need to be closer.' : 'Could not determine reach. Refresh the encounter and try again.';
            return;
        }
        const begin = trapAdapter().ftTrapDisarmBegin;
        const opened = typeof begin === 'function'
            ? await begin({ encounterId: ctx.encounter_id, actorId: ctx.caster_id, hostInventoryId, rollPrivate: ctx.roll_private })
            : null;
        if (!opened || opened.outcome !== 'handled') {
            const target = document.getElementById('div_refresh_side_info');
            const message = !opened ? 'Could not open the trap here. Refresh the encounter and try again.'
                : ['not_armed', 'session_expired', 'host_gone', 'missing_tools', 'tools_out_of_reach', 'not_enough_aps'].includes(opened.error) ? trapErrorMessage(opened.error) : opened.message;
            if (target) target.innerHTML = '<div class="p-3"><p class="text-danger mb-0">' + trapEscapeHtml(message) + '</p></div>';
            return;
        }
        const data = { ...opened.config, ok: true } as unknown as TrapDisarmInitResponse;
        const session = opened.session;

        const hostNow = document.getElementById('div_refresh_side_info');
        if (!hostNow || !hostNow.isConnected) {
            // Replaced while init ran. AP was spent on the attempt — cancel is still the right
            // teardown (safe fail, attempt dropped) rather than a 30-minute dangling session.
            try { void trapAdapter().ftTrapDisarmCancel?.(session, true)?.catch(() => { /* ignore */ }); } catch { /* ignore */ }
            return;
        }

        hostNow.innerHTML = '';
        const wrap = document.createElement('div');
        wrap.className = 'ft-trap-disarm-wrap p-2';
        wrap.tabIndex = 0;
        wrap.innerHTML = trapPanelHtml(data);
        hostNow.appendChild(wrap);

        const panel = new TrapDisarmPanel(wrap, data, session);
        // Claim BEFORE the websocket push our own init opened lands (the AP spend repaint).
        trapClaimSideInfo(hostNow, panel);

        paintTrapGround(wrap, data.ground);

        const stage = wrap.querySelector('#trap_disarm_stage') as HTMLElement | null;
        if (!stage) return;
        // ⚠ Mount on the GEOMETRY SHAPE, not on the classification. Magical traps mint a RUNE
        // board as of 2026-08-05, but a token minted before that switch still carries weave
        // geometry and stays valid for a full 30-minute TTL — mounting the rune board on it would
        // read `runes` off an object that has `threads` and throw in the player's face mid-attempt.
        // The server's result path branches the same way (ft_trap_minigame_board_of), so an
        // in-flight board is never judged by rules it was not built under.
        const geo = data.geometry as Partial<RuneGeometry & MagicGeometry>;
        if (geo && (geo.board === 'rune' || Array.isArray(geo.runes))) {
            mountRuneGame(stage, panel);
        } else if (data.classification === 'magical') {
            mountMagicalGame(stage, panel);
        } else {
            mountMechanicalGame(stage, panel);
        }
        window.dispatchEvent(new CustomEvent('ft:trap-disarm-mounted', { detail: { hostInventoryId, geometry: data.geometry } }));
    } catch (e) {
        console.error(e);
        const host = document.getElementById('div_refresh_side_info');
        if (host) host.innerHTML = '<div class="p-3 text-danger">The disarm panel failed to load.</div>';
    } finally {
        trapOpenInFlight = false;
    }
}

/** Families with baked art. Anything else (or nothing) falls back to the default ground. */
const TRAP_GROUND_FAMILIES = new Set([
    'loam', 'peat', 'sand', 'shingle', 'silt', 'rock', 'cave', 'frost', 'rubble', 'void', 'regolith',
]);
const TRAP_GROUND_DEFAULT = 'loam';

/**
 * Paint the ground the trap is sitting on, behind the mechanism.
 *
 * Uses the excavation board's variant recipe VERBATIM (dig.ts cellHtml): one texture holds four
 * variants in a 2x2 sheet, drawing it at 200% puts exactly one of them in a tile, and the variant is
 * picked from the tile's own coordinates so the surface never looks tiled and a repaint can never
 * reshuffle it. Only level `-4` is used — the 3/2/1 states exist because a dig sweeps soil away, and
 * nothing here erodes.
 *
 * ⛔ A PURE FUNCTION OF THE FAMILY AND THE TILE INDEX. Nothing about the puzzle may reach this —
 * not sear positions, thread order, DC or skill edge. A backdrop that reacted to board state would
 * leak it through a cosmetic layer, which is the same failure the dig's virgin-cell rule prevents.
 */
function paintTrapGround(wrap: HTMLElement, family: string | undefined): void {
    const tiles = wrap.querySelector('.ft-trap-ground-tiles') as HTMLElement | null;
    if (!tiles) return;
    const fam = family && TRAP_GROUND_FAMILIES.has(family) ? family : TRAP_GROUND_DEFAULT;
    const cols = 4;
    const rows = 3;
    tiles.style.gridTemplateColumns = `repeat(${cols}, 1fr)`;
    tiles.style.gridTemplateRows = `repeat(${rows}, 1fr)`;
    const cells: string[] = [];
    for (let z = 0; z < rows; z++) {
        for (let x = 0; x < cols; x++) {
            const variant = (x * 2 + z * 5) % 4;
            // Unquoted url() deliberately: the path has no spaces or parens, and quoting it inside a
            // double-quoted style attribute is an escaping round-trip that has broken twice already.
            const px = (variant % 2) * 100;
            const py = Math.floor(variant / 2) * 100;
            cells.push(`<i style="background-image:url(https://cdn.fatetide.com/images/dig/soil/${fam}-4.png);`
                + `background-size:200% 200%;background-position:${px}% ${py}%"></i>`);
        }
    }
    tiles.innerHTML = cells.join('');
}

function trapPanelHtml(data: TrapDisarmInitResponse): string {
    // 'Unbinding the Glyph' named the WEAVE. A rune board is traced, not unpicked, so it gets its
    // own title — and the weave keeps the old one for as long as pre-promotion tokens can land.
    const title = trapBoardOf(data) === 'rune' ? 'Tracing the Rune'
        : data.classification === 'magical' ? 'Unbinding the Glyph' : 'Disarming the Mechanism';
    const skillWord = data.classification === 'magical' ? 'Magic' : 'Machining';
    const tierNote = data.skill_tier > 0
        ? `${skillWord} tier ${data.skill_tier} — the work reads clearer, and forgives more.`
        : `Untrained ${skillWord.toLowerCase()}: you are working nearly blind. Slow down.`;

    return '<div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">'
        + '<h4 class="h6 mb-0">' + trapEscapeHtml(title) + '</h4>'
        + '<button type="button" class="btn btn-sm btn-outline-secondary" id="trap_disarm_btn_exit" title="Ease off safely. The trap stays armed; the attempt is spent.">Ease Off</button>'
        + '</div>'
        + '<p class="small text-muted mb-2">' + trapEscapeHtml(data.host_name) + '</p>'
        + '<div id="trap_disarm_ground" class="ft-trap-ground">'
        +   '<div class="ft-trap-ground-tiles" aria-hidden="true"></div>'
        +   '<div id="trap_disarm_stage"></div>'
        + '</div>'
        + '<p class="small text-muted mt-2 mb-2" id="trap_disarm_hint"></p>'
        + '<p class="small text-muted mb-2">' + trapEscapeHtml(tierNote) + '</p>'
        + '<div class="mt-2 text-danger small" id="trap_disarm_err" style="display:none;"></div>';
}

window.openTrapDisarmMinigame = openTrapDisarmMinigame;
